import { HTMLAttributes, useMemo, useState } from "react";
import { CommonFlowFrame } from "./common-flow-frame";
import { Button } from "../ui/button";
import { useNavigate } from "@tanstack/react-router";
import { Label } from "@/components/ui/label";
import { Input } from "../ui/input";
import { Controller, FormProvider, useForm, useFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { HelpCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { IdentificationResult } from "@/installFlow/nameplateScan/types";
import { Autocomplete } from "@/components/Autocomplete";
import { ModelSpecsDialog, ModelSpecs } from "@/components/installFlow/ModelSpecsDialog";
import {
  useManufacturers,
  useCreateManufacturer,
  useModels,
  useCreateModel,
  useLookupModelSpecs,
} from "@/equipment/api";
import { useCheckDuplicateInstallation } from "@/installation/api";
import {
  installFlowManufacturer,
  installFlowManufacturerId,
  installFlowModelNumber,
  installFlowModelId,
  installFlowSerialNumber,
  installFlowIdentificationResult,
  installFlowAssetType,
} from "@/installFlow/storage-keys";

const formSchema = z.object({
  manufacturer_id: z.number().nullable(),
  manufacturer_name: z.string().min(1, "Manufacturer is required"),
  model_id: z.number().nullable(),
  model_name: z.string().min(1, "Model Number is required"),
  serial_number: z.string().min(1, "Serial Number is required"),
});

type FormValues = z.infer<typeof formSchema>;

// ─── Manufacturer section ─────────────────────────────────────────────────────

function ManufacturerSection({ onManufacturerChange }: { onManufacturerChange?: () => void }) {
  const { control, setValue, watch } = useFormContext<FormValues>();
  const { data: manufacturersData } = useManufacturers();
  const createManufacturer = useCreateManufacturer();

  const manufacturers = manufacturersData?.results || [];
  const selectedManufacturerId = watch("manufacturer_id");
  const selectedManufacturerName = watch("manufacturer_name");

  const handleSelect = (id: number, name: string) => {
    setValue("manufacturer_id", id);
    setValue("manufacturer_name", name);
    onManufacturerChange?.();
  };

  const handleClear = () => {
    setValue("manufacturer_id", null);
    setValue("manufacturer_name", "");
    onManufacturerChange?.();
  };

  const handleCreate = async (name: string) => {
    try {
      const newManufacturer = await createManufacturer.mutateAsync({ name });
      setValue("manufacturer_id", newManufacturer.id);
      setValue("manufacturer_name", newManufacturer.name);
      onManufacturerChange?.();
    } catch {
      toast.error("Failed to create manufacturer");
      throw new Error("Failed to create manufacturer");
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Label>Manufacturer *</Label>
      <div className="relative">
        <Controller
          name="manufacturer_id"
          control={control}
          render={() => (
            <Autocomplete
              items={manufacturers}
              itemsHeading="All manufacturers"
              selectedId={selectedManufacturerId}
              selectedName={selectedManufacturerName}
              onSelect={handleSelect}
              onClear={handleClear}
              onCreate={handleCreate}
              placeholder="Search manufacturers..."
              emptyMessage="No manufacturers found"
              createLabel="Create"
              isCreating={createManufacturer.isPending}
            />
          )}
        />
      </div>
    </div>
  );
}

// ─── Model section ────────────────────────────────────────────────────────────

function ModelSection({
  manufacturerId,
  manufacturerName,
  disabled,
  ocrMissing,
  onTouched,
  touched,
}: {
  manufacturerId: number | null;
  manufacturerName: string;
  disabled?: boolean;
  ocrMissing: boolean;
  onTouched: () => void;
  touched: boolean;
}) {
  const { control, setValue, watch, formState } = useFormContext<FormValues>();
  const { data: modelsData } = useModels(manufacturerId ?? undefined);

  const currentModelId = watch("model_id");
  const currentModelName = watch("model_name");

  const models = useMemo(
    () => modelsData?.results?.map((m) => ({ id: m.id, name: m.model })) || [],
    [modelsData],
  );

  const handleSelect = (id: number, name: string) => {
    onTouched();
    setValue("model_id", id, { shouldValidate: true });
    setValue("model_name", name, { shouldValidate: true });
  };

  const handleClear = () => {
    onTouched();
    setValue("model_id", null, { shouldValidate: true });
    setValue("model_name", "", { shouldValidate: true });
  };

  const handleCreate = async (name: string) => {
    onTouched();
    setValue("model_id", null, { shouldValidate: true });
    setValue("model_name", name.toUpperCase(), { shouldValidate: true });
  };

  // Yellow warning: OCR couldn't read the model and user hasn't touched it yet
  const showOcrWarning = ocrMissing && !touched && !currentModelName;
  // Red error: user has touched the field and it's still empty
  const showRequiredError = touched && !currentModelName && formState.errors.model_name;

  return (
    <div className={`flex flex-col gap-1.5 transition-opacity ${disabled ? "opacity-50" : ""}`}>
      <Label>Model Number *</Label>
      <div className="relative">
        <Controller
          name="model_id"
          control={control}
          render={() => (
            <Autocomplete
              items={models}
              itemsHeading="All models"
              selectedId={currentModelId}
              selectedName={currentModelName}
              onSelect={handleSelect}
              onClear={handleClear}
              onCreate={manufacturerName ? handleCreate : undefined}
              placeholder={manufacturerName ? "Search models..." : "Select a manufacturer first"}
              emptyMessage={manufacturerName ? "No models found" : "Select a manufacturer first"}
              createLabel="Create"
              disabled={disabled || !manufacturerId}
            />
          )}
        />
      </div>
      {showOcrWarning && (
        <p className="text-sm font-medium text-yellow-600">
          We had trouble reading the model number
        </p>
      )}
      {showRequiredError && (
        <p className="text-sm font-medium text-destructive">
          {formState.errors.model_name?.message}
        </p>
      )}
    </div>
  );
}

// ─── Read identification result from localStorage synchronously ───────────────

function readIdentificationResult(): IdentificationResult | null {
  try {
    const stored = installFlowIdentificationResult.get();
    if (!stored) return null;
    return JSON.parse(stored) as IdentificationResult;
  } catch {
    return null;
  }
}

type InitialValues = FormValues & { _modelOcrMissing: boolean };

function buildInitialValues(result: IdentificationResult | null): InitialValues {
  if (!result) {
    return {
      manufacturer_id: null,
      manufacturer_name: "",
      model_id: null,
      model_name: "",
      serial_number: "",
      _modelOcrMissing: false,
    };
  }

  const manufacturer_id = result.manufacturer_id ?? null;
  const manufacturer_name = result.manufacturer_name ?? result.manufacturer_name_raw ?? "";

  let model_id: number | null = null;
  let model_name = "";
  let modelOcrMissing = false;

  if (result.equipment_model_id) {
    model_id = result.equipment_model_id;
    model_name = (result.equipment_model_name ?? "").toUpperCase();
  } else if (result.model_number_raw) {
    model_name = result.model_number_raw.toUpperCase();
  } else {
    modelOcrMissing = true;
  }

  return {
    manufacturer_id,
    manufacturer_name,
    model_id,
    model_name,
    serial_number: (result.serial_number ?? "").toUpperCase(),
    _modelOcrMissing: modelOcrMissing,
  };
}

// ─── Main component ───────────────────────────────────────────────────────────

interface AuthProps extends HTMLAttributes<HTMLDivElement> {}

export function ReviewNamePlateInfo({ className }: AuthProps) {
  const navigate = useNavigate();
  const createModel = useCreateModel();
  const lookupSpecs = useLookupModelSpecs();

  const [showSpecsDialog, setShowSpecsDialog] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState<{
    manufacturer_id: number;
    manufacturer_name: string;
    model_name: string;
    serial_number: string;
  } | null>(null);

  // Read from localStorage once synchronously so form mounts with the right values,
  // ensuring Autocomplete receives selectedName/selectedId on its first render.
  const { _modelOcrMissing: initialModelOcrMissing, ...initialValues } = useMemo<InitialValues>(
    () => buildInitialValues(readIdentificationResult()),
    [],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: initialValues,
  });

  // true = OCR ran but returned no model value; false = not yet checked or value was found
  const [modelOcrMissing] = useState(initialModelOcrMissing);
  // true = user has touched/changed the model field at least once
  const [modelTouched, setModelTouched] = useState(false);
  const checkDuplicate = useCheckDuplicateInstallation();

  // Persist and navigate after a model id is resolved
  const persistAndNavigate = async (data: FormValues) => {
    installFlowManufacturer.set(data.manufacturer_name);
    installFlowManufacturerId.set(String(data.manufacturer_id ?? ""));
    installFlowModelNumber.set(data.model_name);
    installFlowModelId.set(String(data.model_id ?? ""));
    installFlowSerialNumber.set(data.serial_number.toUpperCase());
    installFlowIdentificationResult.remove();

    const isDuplicate = await checkDuplicate.mutateAsync({
      serialNumber: data.serial_number,
      manufacturerName: data.manufacturer_name,
      modelName: data.model_name,
    });
    if (isDuplicate) {
      await navigate({ to: "/installFlow/match-existing-asset" });
    } else {
      await navigate({ to: "/installFlow/asset-name" });
    }
  };

  const handleSubmit = async () => {
    const data = form.getValues();

    if (!data.model_name) {
      toast.error("Model Number is required");
      return;
    }

    // Existing model — check duplicates then navigate
    if (data.model_id) {
      await persistAndNavigate(data);
      return;
    }

    // New model name typed — need specs confirmation
    if (!data.manufacturer_id) {
      toast.error("Manufacturer is required to create a new model");
      return;
    }

    setPendingSubmitData({
      manufacturer_id: data.manufacturer_id,
      manufacturer_name: data.manufacturer_name,
      model_name: data.model_name,
      serial_number: data.serial_number,
    });
    setShowSpecsDialog(true);

    lookupSpecs.mutate({
      manufacturer_name: data.manufacturer_name,
      model_name: data.model_name,
    });
  };

  const handleSpecsConfirm = async (specs: ModelSpecs) => {
    if (!pendingSubmitData) return;
    try {
      const newModel = await createModel.mutateAsync({
        name: pendingSubmitData.model_name,
        model: pendingSubmitData.model_name,
        manufacturer_id: pendingSubmitData.manufacturer_id,
        asset_type: specs.asset_type,
        phase_count: specs.phase_count ?? undefined,
        refrigerant_type: specs.refrigerant_type ?? undefined,
        nameplate_capacity: specs.nameplate_capacity_btu
          ? specs.nameplate_capacity_btu / 1000
          : undefined,
      });
      installFlowAssetType.set(specs.asset_type);
      form.setValue("model_id", newModel.id);
      await persistAndNavigate({ ...form.getValues(), model_id: newModel.id });
    } catch {
      toast.error("Failed to create model. Please try again.");
    } finally {
      setShowSpecsDialog(false);
      setPendingSubmitData(null);
      lookupSpecs.reset();
    }
  };

  const handleDialogCancel = () => {
    setShowSpecsDialog(false);
    setPendingSubmitData(null);
    lookupSpecs.reset();
  };

  const manufacturerId = form.watch("manufacturer_id");
  const manufacturerName = form.watch("manufacturer_name");
  // Unlock model as soon as any manufacturer name is present — same behaviour as DetailsStep
  const hasManufacturer = !!manufacturerName;
  const modelName = form.watch("model_name");
  const isSubmitting = createModel.isPending || checkDuplicate.isPending;
  const canSubmit = !!modelName && !isSubmitting && !showSpecsDialog;

  return (
    <>
      <CommonFlowFrame>
        <div className="w-full h-full flex flex-col gap-6 pt-6">
          <div className="text-left">
            <h2 className="text-xl font-bold text-black-700 flex items-center gap-2">
              Review Name Plate Information
              <HelpCircle
                size={20}
                color="black"
                onClick={() => navigate({ to: "/installFlow/match-existing-asset" })}
              />
            </h2>
            <p className="text-md font-semibold text-gray-600">
              Make sure everything looks correct before continuing.
            </p>
          </div>

          <FormProvider {...form}>
            <form
              onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
              className="flex flex-col gap-4 w-full"
            >
              <ManufacturerSection
                onManufacturerChange={() => {
                  form.setValue("model_id", null);
                  form.setValue("model_name", "");
                }}
              />

              <ModelSection
                manufacturerId={manufacturerId}
                manufacturerName={manufacturerName}
                disabled={!hasManufacturer}
                ocrMissing={modelOcrMissing}
                touched={modelTouched}
                onTouched={() => setModelTouched(true)}
              />

              {/* Serial Number — plain input, uppercase enforced */}
              <div className="flex flex-col gap-1.5">
                <Label>Serial Number *</Label>
                <Input
                  placeholder="Serial Number"
                  className="h-12"
                  value={form.watch("serial_number")}
                  onChange={(e) =>
                    form.setValue("serial_number", e.target.value.toUpperCase(), {
                      shouldValidate: true,
                    })
                  }
                />
                {form.formState.errors.serial_number && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.serial_number.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                variant="installFlow"
                className="h-[54px] w-full mt-2"
                disabled={!canSubmit}
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Next"
                )}
              </Button>
            </form>
          </FormProvider>
        </div>
      </CommonFlowFrame>

      <ModelSpecsDialog
        open={showSpecsDialog}
        onOpenChange={handleDialogCancel}
        manufacturerName={pendingSubmitData?.manufacturer_name || ""}
        modelName={pendingSubmitData?.model_name || ""}
        isLoading={lookupSpecs.isPending}
        lookupResult={lookupSpecs.data}
        lookupFailed={lookupSpecs.isError}
        onConfirm={handleSpecsConfirm}
      />
    </>
  );
}
